import {
  GeofenceStateParam,
  HealthStatus,
  SafetyAlertParams,
  SoftwareStatus,
} from '@casops/store/apis/commonModels';
import { FiltersState } from '../slices/filtersSlice';

export enum QueryParamNames {
  AssetIds = 'assets',
  AssetGroups = 'ags',
  ConfigGroups = 'cgs',
  DeviceTypes = 'hws',
  ObjectTypes = 'objs',
  SoftwareStatus = 'sw',
  HealthStatus = 'hlth',
  TimeRangeEnd = 'end',
  TimeRangeStart = 'start',
  GeofenceAreas = 'geofas',
  GeofenceStyles = 'geofsy',
  GeofenceTypes = 'geofts',
  GeofenceState = 'geost',
  SafetyAlertType = 'sat',
  SafetyAlertAcknowledged = 'ack',
}

export interface CustomTimeRangeParams {
  timeRangeStart: string;
  timeRangeEnd: string;
}

export interface QueryParams extends FiltersState, CustomTimeRangeParams, SafetyAlertParams {
  assetIds: string; // Comma-separated string of IDs.
}

export const getUrlParams = <T extends Partial<QueryParams>>({
  assetGroups,
  assetIds,
  configGroups,
  deviceTypes,
  geofenceAreas,
  geofenceState,
  geofenceStyles,
  geofenceTypes,
  healthStatus,
  objectTypes,
  softwareStatus,
  timeRangeEnd,
  timeRangeStart,
  safetyAlertType,
  safetyAlertAcknowledged,
}: T) => {
  const queryParams = new URLSearchParams();

  if (assetGroups?.length) {
    queryParams.append(QueryParamNames.AssetGroups, assetGroups.join(','));
  }

  if (assetIds?.length) {
    queryParams.append(QueryParamNames.AssetIds, assetIds);
  }

  if (configGroups?.length) {
    queryParams.append(QueryParamNames.ConfigGroups, configGroups.join(','));
  }

  if (deviceTypes?.length) {
    queryParams.append(QueryParamNames.DeviceTypes, deviceTypes.join(','));
  }

  if (geofenceAreas?.length) {
    queryParams.append(QueryParamNames.GeofenceAreas, geofenceAreas.join(','));
  }

  if (geofenceState && geofenceState !== GeofenceStateParam.ShowAll) {
    queryParams.append(QueryParamNames.GeofenceState, geofenceState);
  }

  if (geofenceStyles?.length) {
    queryParams.append(QueryParamNames.GeofenceStyles, geofenceStyles.join(','));
  }

  if (geofenceTypes?.length) {
    queryParams.append(QueryParamNames.GeofenceTypes, geofenceTypes.join(','));
  }

  if (objectTypes?.length) {
    queryParams.append(QueryParamNames.ObjectTypes, objectTypes.join(','));
  }

  if (softwareStatus && softwareStatus !== SoftwareStatus.ShowAll) {
    queryParams.append(QueryParamNames.SoftwareStatus, softwareStatus);
  }

  if (healthStatus && healthStatus !== HealthStatus.ShowAll) {
    queryParams.append(QueryParamNames.HealthStatus, healthStatus);
  }

  // The custom time range params must only be sent if they are both defined.
  if (timeRangeStart && timeRangeEnd) {
    queryParams.append(QueryParamNames.TimeRangeStart, timeRangeStart);
    queryParams.append(QueryParamNames.TimeRangeEnd, timeRangeEnd);
  }

  if (safetyAlertType) {
    queryParams.append(QueryParamNames.SafetyAlertType, safetyAlertType);
  }

  if (typeof safetyAlertAcknowledged === 'boolean') {
    queryParams.append(
      QueryParamNames.SafetyAlertAcknowledged,
      safetyAlertAcknowledged ? 'true' : 'false',
    );
  }

  return queryParams;
};
